// src/index.ts
import { z } from "zod";

// src/app-pages.ts
var parsePeriod = (period) => {
  const match = /^(\d{4})-(\d{2})$/.exec(period);
  if (!match) return void 0;
  const year = Number(match[1]);
  const month = Number(match[2]);
  if (year < 2e3 || year > 2100 || month < 1 || month > 12) return void 0;
  return { year, month };
};
var toPeriod = (year, month) => `${year}-${String(month).padStart(2, "0")}`;
var periodFormat = new Intl.DateTimeFormat("en-US", {
  month: "short",
  year: "numeric",
  timeZone: "UTC"
});
var formatPeriod = (year, month) => periodFormat.format(new Date(Date.UTC(year, month - 1, 1)));
var INVOICE_TABS = ["all", "late", "anomaly"];

// src/index.ts
var AGENT_MAX_MESSAGE_LENGTH = 8e3;
var AGENT_THREAD_LIST_LIMIT = 30;
var AGENT_THREAD_TITLE_MAX_LENGTH = 80;
var AGENT_MAX_THREAD_FILES = 20;
var AGENT_MAX_ATTACHMENT_BYTES = 8 * 1024 * 1024;
var AGENT_FILE_NAME_MAX_LENGTH = 255;
var AGENT_MAX_ATTACHMENT_CHARS = 4e4;
var AGENT_ATTACHMENT_MIME_TYPES = [
  "text/csv",
  "text/plain",
  "text/markdown",
  "application/pdf",
  // .docx
  "application/vnd.openxmlformats-officedocument.wordprocessingml.document",
  // .xlsx
  "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet"
];
var isAttachmentMimeType = (value) => AGENT_ATTACHMENT_MIME_TYPES.includes(value);
var AGENT_ATTACHMENT_EXTENSIONS = [
  ".csv",
  ".txt",
  ".md",
  ".pdf",
  ".docx",
  ".xlsx"
];
var agentFileSchema = z.object({
  id: z.string(),
  name: z.string().min(1).max(AGENT_FILE_NAME_MAX_LENGTH),
  mimeType: z.string().min(1),
  size: z.number().int().nonnegative(),
  source: z.enum(["user", "agent"]),
  createdAt: z.iso.datetime()
});
var agentThreadFilesResponseSchema = z.object({
  threadId: z.string(),
  files: z.array(agentFileSchema)
});
var agentThreadFileParamsSchema = z.object({
  threadId: z.string().min(1).max(128),
  name: z.string().min(1).max(AGENT_FILE_NAME_MAX_LENGTH)
});
var agentMessageSchema = z.object({
  role: z.enum(["user", "assistant"]),
  content: z.string().min(1).max(AGENT_MAX_MESSAGE_LENGTH)
});
var agentRunRequestSchema = z.object({
  organizationId: z.string().min(1),
  /**
   * The conversation this turn belongs to.
   *
   * Optional, but the client is expected to mint one rather than wait for the server:
   * a file can be attached before the opening turn is sent, and it has to be filed under
   * *something*. An id the server has not seen is a new conversation, not an attack —
   * Mastra creates the thread on first write, owned by whoever sent it. Omitting it still
   * works for a plain text turn, in which case the server mints one and reports it in the
   * `thread` frame.
   *
   * Threads are owned by the user who created them; a mismatched id is rejected with 403
   * rather than silently starting a new one.
   */
  threadId: z.string().min(1).max(128).optional(),
  message: z.string().min(1).max(AGENT_MAX_MESSAGE_LENGTH),
  /**
   * The sender's IANA time zone ("Europe/Kyiv"), so "today" and "this month" are theirs
   * rather than the server's. Not checked against a list: a zone the server's runtime does
   * not know yet falls back to UTC there, rather than failing the whole request here.
   */
  timeZone: z.string().min(1).max(64).optional()
});
var AGENT_ERROR_CODES = [
  "not_configured",
  "model_not_configured",
  "rate_limited",
  "provider_auth",
  "provider_unavailable",
  "timeout",
  "context_too_long",
  "content_filtered",
  "tool_failed",
  "unknown"
];
var agentErrorCodeSchema = z.enum(AGENT_ERROR_CODES);
var AGENT_ERROR_MESSAGES = {
  not_configured: "The assistant is not configured on this server.",
  model_not_configured: "No AI model is set up for your organization yet. Ask an organization admin to add one under Admin \u2192 AI models.",
  rate_limited: "The assistant is busy right now. Try again in a few seconds.",
  provider_auth: "The assistant could not reach its model. Please contact an administrator.",
  provider_unavailable: "The assistant is temporarily unavailable. Try again shortly.",
  timeout: "The assistant took too long to respond. Try again.",
  context_too_long: "This conversation has grown too long. Start a new one to continue.",
  content_filtered: "The assistant could not answer that. Try rephrasing your question.",
  tool_failed: "The assistant could not look up the information it needed. Try again.",
  unknown: "The assistant failed to complete this run."
};
var agentNavigationSchema = z.object({
  to: z.string().startsWith("/$slug"),
  /** Every other `$param` in `to`, already checked against the organization's data. */
  params: z.record(z.string(), z.string()),
  /**
   * Filters the page reads from its URL: `?aircraftId=…&tab=late`. Checked like `params`.
   * Absent when the link sets none, so the page opens on its own defaults.
   */
  search: z.record(z.string(), z.string()).optional(),
  /** Where it leads, written for the reader: "Invoices › Sep 2026 · N521AJ · Late". */
  label: z.string().min(1)
});
var agentNavigationKey = ({ to, params, search }) => JSON.stringify([to, params, search ?? {}]);
var agentRunEventSchema = z.discriminatedUnion("type", [
  /** Always the first frame: the thread this run belongs to. Echoed back on the next
   *  request to continue the conversation. */
  z.object({ type: z.literal("thread"), threadId: z.string() }),
  z.object({ type: z.literal("text-delta"), text: z.string() }),
  z.object({ type: z.literal("tool-call"), toolName: z.string() }),
  z.object({ type: z.literal("tool-result"), toolName: z.string() }),
  /** Follows the tool result it came from; a run may suggest more than one page. */
  z.object({ type: z.literal("navigation"), navigation: agentNavigationSchema }),
  z.object({
    type: z.literal("done"),
    toolCallCount: z.number(),
    model: z.string(),
    durationMs: z.number()
  }),
  z.object({
    type: z.literal("error"),
    /** Already written for a reader — one of `AGENT_ERROR_MESSAGES`. Render it as-is. */
    message: z.string(),
    /**
     * Tolerant rather than strict, both of them. A frame from a server that predates these
     * fields, or one carrying a code this client does not know yet, must still parse:
     * `parseFrame` drops anything that fails validation, and dropping *this* frame would
     * leave the run looking like it is still streaming with no notice that it failed.
     */
    code: agentErrorCodeSchema.catch("unknown"),
    retryable: z.boolean().default(false),
    /** From the provider's `Retry-After`, when it sent one. Advisory. */
    retryAfterSeconds: z.number().int().positive().optional()
  })
]);
var agentThreadSummarySchema = z.object({
  id: z.string(),
  /** Derived from the opening turn when the thread was created. Never empty. */
  title: z.string(),
  updatedAt: z.iso.datetime()
});
var agentHistoryMessageSchema = z.object({
  id: z.string(),
  role: z.enum(["user", "assistant"]),
  /** Empty for a turn that only suggested pages. */
  content: z.string(),
  /** Pages this turn suggested, replayed so a reply that mentions a link still has it. */
  navigations: z.array(agentNavigationSchema).optional(),
  createdAt: z.iso.datetime()
});
var agentThreadListResponseSchema = z.object({
  threads: z.array(agentThreadSummarySchema)
});
var agentThreadMessagesResponseSchema = z.object({
  threadId: z.string(),
  messages: z.array(agentHistoryMessageSchema)
});
var agentThreadParamsSchema = z.object({
  threadId: z.string().min(1).max(128)
});
export {
  AGENT_ATTACHMENT_EXTENSIONS,
  AGENT_ATTACHMENT_MIME_TYPES,
  AGENT_ERROR_CODES,
  AGENT_ERROR_MESSAGES,
  AGENT_FILE_NAME_MAX_LENGTH,
  AGENT_MAX_ATTACHMENT_BYTES,
  AGENT_MAX_ATTACHMENT_CHARS,
  AGENT_MAX_MESSAGE_LENGTH,
  AGENT_MAX_THREAD_FILES,
  AGENT_THREAD_LIST_LIMIT,
  AGENT_THREAD_TITLE_MAX_LENGTH,
  INVOICE_TABS,
  agentErrorCodeSchema,
  agentFileSchema,
  agentHistoryMessageSchema,
  agentMessageSchema,
  agentNavigationKey,
  agentNavigationSchema,
  agentRunEventSchema,
  agentRunRequestSchema,
  agentThreadFileParamsSchema,
  agentThreadFilesResponseSchema,
  agentThreadListResponseSchema,
  agentThreadMessagesResponseSchema,
  agentThreadParamsSchema,
  agentThreadSummarySchema,
  formatPeriod,
  isAttachmentMimeType,
  parsePeriod,
  toPeriod
};
